import {
  Card,
  Container,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  debounce,
} from '@mui/material';
import CaseRow from './CaseRow';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import Page from 'src/components/Page';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import useSettings from 'src/careflow/ph-core/multi-tenant/setting/useSettings';
import { CaseManager } from 'src/@types/crs/referral';
import { LoadingTableRow, TableHeadCustom } from 'src/components/table';
import { PaginateQuery, PaginateQueryWithStats } from 'src/api/pagination/dtos';
import { CaseService, CaseStat, PaginatedCaseDto } from 'src/crs/case/service/CaseService';
import FilterDrawer from 'src/components/FilterDrawer';
import { Tooltip } from '@mui/material';
import CaseFiltersList, {
  CaseFilterOption,
  CaseFiltersListValues,
} from 'src/careflow/care-coordination/case/detail/components/CaseFiltersList';
import { wait } from 'src/utils/timers';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useNotiMessage from 'src/hooks/useNotiMessage';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { sortBy } from 'lodash';
import { UserService } from 'src/careflow/ph-core/admin/user/UserService';
import useTable from 'src/hooks/useTable';
import { caseDtoToCaseManager } from 'src/careflow/common/common-utils';
import { TableToolbar } from 'src/careflow/common';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { translateTableHeadCustom } from 'src/careflow/helpers/common';
import { TABLE_HEAD_CASES_LIST } from 'src/careflow/common/table-head';
import NoResultsTableRow from 'src/careflow/common/NoResultsTableRow';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import StandaloneCustomPagination from 'src/careflow/common/StandaloneCustomPagination';
import { defaultRowsPerPageOptions } from 'src/careflow/helpers/constants';

interface CaseListProps {
  includeBreadcrumbs;
  tableData: PaginatedCaseDto | undefined | null;
  setTableData: Dispatch<SetStateAction<PaginatedCaseDto | undefined | null>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  assignedTo: any;
  setAssignedTo: Dispatch<SetStateAction<any>>;
  setStats?: Dispatch<SetStateAction<any>>;
  caseStatus: string;
  setCaseStatus: Dispatch<SetStateAction<string>>;
  workflowStageName?: string;
  setWorkflowStageName?: Dispatch<SetStateAction<string>>;
  show?: boolean;
  showFilters?: boolean;
  totalItems: number | null;
  setTotalItems: Dispatch<SetStateAction<number | null>>;
}

export default function CaseList({
  includeBreadcrumbs,
  tableData,
  setTableData,
  search,
  setSearch,
  isLoading,
  setIsLoading,
  assignedTo,
  setAssignedTo,
  setStats,
  caseStatus,
  setCaseStatus,
  workflowStageName = '',
  setWorkflowStageName,
  show = true,
  showFilters = true,
  totalItems,
  setTotalItems,
}: CaseListProps) {
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage, setPage } = useTable({
    defaultRowsPerPage: 5,
    defaultOrderBy: 'id',
    initialIndex: 1,
    defaultCurrentPage: 1,
  });
  

  const { themeStretch } = useSettings();
  const [isOpenAplyFilters, setIsOpenAplyFilters] = useState<boolean>(false);

  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [stageOptions, setStageOptions] = useState<CaseFilterOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<CaseFilterOption[]>([]);
  const [draftFilters, setDraftFilters] = useState<CaseFiltersListValues>({
    workflowStatus: '',
    workflowStageName: '',
    assignedTo: [],
  });
  const { i18n } = useLocales();
  const { showErrorMessage } = useNotiMessage();

  const authUser = useAuth().getCurrentUser();

  const userOrganizations = useMemo(() => authUser?.organizations ?? [], [authUser?.organizations]);

  const orgsKey = useMemo(() => JSON.stringify(userOrganizations), [userOrganizations]);

  const getPaginateQuery = (
    page: number,
    rowsPerPage: number,
    search: string,
    assignedTo: any,
    caseStatus: string,
    stage: string
  ) => {
    const paginateQuery: PaginateQueryWithStats = { page: page, limit: rowsPerPage };
    if (search) {
      paginateQuery.search = search;
    }
    paginateQuery.filter = {};

    if (assignedTo?.length) {
      paginateQuery.filter.workflowOwnedBy = `$in:${assignedTo?.join(',')}`;
    }

    if (caseStatus) {
      paginateQuery.filter.workflowStatus = `$eq:${caseStatus}`;
    }

    if (stage) {
      paginateQuery.filter.workflowStageName = `$eq:${stage}`;
    }

    return paginateQuery;
  };

  const fetchCaseList = async (
    page: number,
    rowsPerPage: number,
    search: string,
    assignedTo: any,
    caseStatus: string,
    stage: string
  ) => {
    setIsLoading(true);
    await wait();

    const cases = await CaseService.getAll(
      getPaginateQuery(page, rowsPerPage, search, assignedTo, caseStatus, stage)
    );

    if (!cases) {
      setIsLoading(false);
      return showErrorMessage(i18n('error.case.list'));
    }

    setTableData(cases);
    if (setStats && cases?.stats) {
      setStats(cases?.stats);
    }

    if (cases?.meta?.totalItems !== undefined) {
      setTotalItems(cases?.meta?.totalItems);
    } else {
      setTotalItems(0);
    }
    setIsLoading(false);
    if (cases?.stats && Array.isArray(cases.stats)) {
      setStatusOptions(
        cases.stats
          .filter((s: CaseStat) => !!s?.code)
          .map((s: CaseStat) => ({
            value: s?.code ?? '',
            label: s?.label ?? s?.code ?? '',
          }))
      );
    }
    if (cases?.stageStats && Array.isArray(cases.stageStats)) {
      setStageOptions(
        cases.stageStats.map((s: CaseStat) => ({
          value: s?.code ?? '',
          label: s?.label ?? s?.code ?? '',
        }))
      );
    }
  };

  const getCaseList = useCallback(debounce(fetchCaseList, 600), []);

  useEffect(() => {
    getCaseList(page, rowsPerPage, search, assignedTo, caseStatus, workflowStageName);
    getAllUsers();
  }, [page, rowsPerPage, search, assignedTo, caseStatus, workflowStageName, orgsKey]);

  const handleRowsPerPageChange = (newSize: number) => {
    onChangeRowsPerPage({ target: { value: String(newSize) } } as React.ChangeEvent<HTMLInputElement>);
  };

  const getAllUsers = async () => {
    const result = await UserService.getUsersByOrgAndRole(userOrganizations, [])
    setAllUsers(sortBy(result.filter((e: any) => e.status === 'Active'), ['userName']));
  };

  const handleSearch = (query: string) => {
    setSearch(query);
    setPage(1);
  };

  const handleOpenFilterDrawer = () => {
    setDraftFilters({
      workflowStatus: caseStatus,
      workflowStageName,
      assignedTo: assignedTo ?? [],
    });
    setIsOpenAplyFilters(true);
  };

  const handleApplyFilters = () => {
    setCaseStatus(draftFilters.workflowStatus);
    setWorkflowStageName?.(draftFilters.workflowStageName);
    setAssignedTo(draftFilters.assignedTo);
    setPage(1);
    setIsOpenAplyFilters(false);
  };

  const handleClearAllFilters = () => {
    setDraftFilters({ workflowStatus: '', workflowStageName: '', assignedTo: [] });
    setCaseStatus('');
    setWorkflowStageName?.('');
    setAssignedTo([]);
    setPage(1);
    setIsOpenAplyFilters(false);
  };

  const dataFormatted = (): CaseManager[] => {
    const data = tableData?.data ? tableData.data.map((r) => caseDtoToCaseManager(r)) : [];
    return data;
  };

  const rows = dataFormatted();

  if (!show) {
    return null;
  }

  return (
    <Page title={i18n('case.list.title', 'crs')}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        {includeBreadcrumbs ? (
          <HeaderBreadcrumbs
            title={i18n('case.list.title', 'crs')}
            heading=""
            links={[
              { name: `${i18n('admin.list.dashboard')}`, href: PATH_DASHBOARD.root },
              { name: `${i18n('breadcrumbs.title', 'crs')}` },
              { name: `${i18n('case.list.title', 'crs')}`, href: PATH_DASHBOARD.crs.case },
            ]}
          />
        ) : null}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <Stack direction="row" sx={{ py: 2, px: 2 }}>
                <Grid xs={6}>
                  <TableToolbar filterMrn={search} onFilterMrn={handleSearch} />
                </Grid>
                <Grid container alignItems="center" justifyContent="flex-end" xs={6}>
                  <Tooltip title="Filter">
                    {showFilters ? (
                      <IconButton onClick={handleOpenFilterDrawer} sx={{ height: '40px' }}>
                        <FilterListIcon htmlColor="#8f9993" />
                      </IconButton>
                    ) : (
                      <></>
                    )}
                  </Tooltip>
                </Grid>
              </Stack>
              <TableContainer>
                <Table>
                  <TableHeadCustom
                    headLabel={translateTableHeadCustom(i18n, TABLE_HEAD_CASES_LIST, 'crs')}
                  />
                  <TableBody>
                    {!isLoading ? (
                      rows?.length ? (
                        rows.map((row) => <CaseRow key={row.id} row={row} />)
                      ) : (
                        <NoResultsTableRow
                          colSpan={TABLE_HEAD_CASES_LIST.length}
                          text={i18n('case.list.listNotFound', 'crs')}
                        />
                      )
                    ) : (
                      <LoadingTableRow colSpan={TABLE_HEAD_CASES_LIST.length} />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid xs={12}>
                <Stack justifyContent="flex-end">
                  <StandaloneCustomPagination
                    page={page}
                    totalItems={tableData ? tableData.meta.totalItems : 0}
                    pageSize={rowsPerPage}
                    rowsPerPageOptions={defaultRowsPerPageOptions}
                    onPageChange={(newPage) => onChangePage(null, newPage)}
                    onPageSizeChange={handleRowsPerPageChange}
                  />
                </Stack>
              </Grid>
            </Card>
          </Grid>
          {showFilters ? (
            <FilterDrawer
              onApplyButtonClick={handleApplyFilters}
              title={i18n('case.list.filterPopUp.title', 'crs')}
              onCloseIconButtonClick={() => {
                setIsOpenAplyFilters(false);
              }}
              onClearAllButtonClick={handleClearAllFilters}
              anchor={'right'}
              open={isOpenAplyFilters}
            >
              <CaseFiltersList
                values={draftFilters}
                onChange={(key, value) =>
                  setDraftFilters((prev) => ({ ...prev, [key]: value }))
                }
                statusOptions={statusOptions}
                stageOptions={stageOptions}
                users={allUsers}
                i18n={i18n}
              />
            </FilterDrawer>
          ) : null}
        </Grid>
      </Container>
    </Page>
  );
}
